import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Button } from '../ui/button';
import { ArrowRight, Sparkles, Shield, Users, Globe } from 'lucide-react';

export const CTASection = () => {
  const { t } = useTranslation();
  
  const trustIndicators = [
    { icon: Shield, textKey: 'home.cta.buyerProtection' },
    { icon: Users, textKey: 'home.cta.verifiedSuppliers' },
    { icon: Globe, textKey: 'home.cta.countries' },
  ];

  return (
    <section className="relative py-32 overflow-hidden" data-testid="cta-section">
      {/* Premium Dark Background */}
      <div className="absolute inset-0 bg-[#0A0A0A]" />
      
      {/* Gradient Orbs */}
      <div className="absolute top-0 left-1/4 w-[600px] h-[600px] bg-[#C25E30]/20 rounded-full blur-[150px]" />
      <div className="absolute bottom-0 right-1/4 w-[400px] h-[400px] bg-[#1E3A8A]/20 rounded-full blur-[120px]" />
      
      {/* Grid Pattern */}
      <div className="absolute inset-0 opacity-[0.03]">
        <div className="absolute inset-0" style={{
          backgroundImage: `linear-gradient(white 1px, transparent 1px), linear-gradient(90deg, white 1px, transparent 1px)`,
          backgroundSize: '60px 60px',
        }} />
      </div>

      <div className="max-w-5xl mx-auto px-4 sm:px-8 lg:px-12 text-center relative">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="space-y-8"
        >
          {/* Badge */}
          <motion.div 
            initial={{ opacity: 0, scale: 0.9 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            transition={{ delay: 0.1 }}
            className="inline-flex items-center gap-2 bg-[#C25E30]/10 text-[#C25E30] px-5 py-2.5 rounded-full text-sm font-semibold border border-[#C25E30]/20"
          >
            <Sparkles className="w-4 h-4" />
            {t('home.cta.badge')}
          </motion.div>
          
          {/* Heading */}
          <h2 className="text-4xl sm:text-5xl lg:text-6xl text-white tracking-tight leading-[1.1]">
            {t('home.cta.title')}{' '}
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#C25E30] to-[#E8A54B]">
              {t('home.cta.titleHighlight')}
            </span>?
          </h2>
          
          {/* Description */}
          <p className="text-xl text-white/50 max-w-2xl mx-auto">
            {t('home.cta.description')}
          </p>
          
          {/* Trust Indicators */}
          <div className="flex flex-wrap justify-center gap-8 py-4">
            {trustIndicators.map((item) => (
              <div key={item.textKey} className="flex items-center gap-2 text-white/40">
                <item.icon className="w-5 h-5 text-[#C25E30]" />
                <span className="text-sm font-medium">{t(item.textKey)}</span>
              </div>
            ))}
          </div>
          
          {/* CTA Buttons */}
          <div className="flex flex-col sm:flex-row gap-5 justify-center pt-4">
            <Link to="/register?role=buyer">
              <Button 
                size="lg" 
                className="bg-[#C25E30] hover:bg-[#A64D24] text-white btn-premium gap-3 px-10 py-7 text-lg font-semibold rounded-xl w-full sm:w-auto" 
                data-testid="cta-buyer-btn"
              >
                {t('home.cta.startBuyer')}
                <ArrowRight className="w-5 h-5" />
              </Button>
            </Link>
            <Link to="/register?role=supplier">
              <Button 
                size="lg" 
                variant="outline" 
                className="border-2 border-white/20 text-white hover:bg-white/10 backdrop-blur-sm px-10 py-7 text-lg font-semibold rounded-xl transition-all w-full sm:w-auto" 
                data-testid="cta-supplier-btn"
              >
                {t('home.cta.joinSupplier')}
              </Button>
            </Link>
          </div>
          
          {/* Bottom Note */}
          <p className="text-white/30 text-sm">
            {t('home.cta.bottomNote')}
          </p>
        </motion.div>
      </div>
    </section>
  );
};
